
import React, { useMemo, useState, useCallback } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceArea, ReferenceLine } from 'recharts';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { TrendingUp, TrendingDown, Target, ZoomIn, ZoomOut, RotateCcw } from 'lucide-react';

interface ActualGrowthPoint {
  position: number;
  growth: number;
  actual: number;
  change: number;
  cumulative: number;
  prediction?: string;
}

interface ActualGrowthChartProps {
  positionsData: Array<{
    position: number;
    prediction: 'WIN' | 'LOSS' | 'SKIP';
    actualResult: number;
    isCorrect?: boolean;
  }>;
  tpRatio?: number;
  slRatio?: number;
}

export const ActualGrowthChart: React.FC<ActualGrowthChartProps> = ({ positionsData, tpRatio = 2, slRatio = 1 }) => {
  const [zoomDomain, setZoomDomain] = useState<{left?: number; right?: number; top?: number; bottom?: number} | null>(null);
  const [refAreaLeft, setRefAreaLeft] = useState<number | null>(null);
  const [refAreaRight, setRefAreaRight] = useState<number | null>(null);

  const { growthData, stats } = useMemo(() => {
    // Process ALL positions (including SKIP) for actual market performance
    let cumulativeGrowth = 0;
    let maxGrowth = 0;
    let minGrowth = 0;
    let totalPositions = 0;
    let marketWins = 0;
    let marketLosses = 0;

    const data: ActualGrowthPoint[] = positionsData.map((position) => {
      let change = 0;
      
      totalPositions++;
      if (position.actualResult === 1) {
        change = tpRatio; // Use TP ratio for wins
        marketWins++;
      } else if (position.actualResult === 0) {
        change = -slRatio; // Use SL ratio for losses
        marketLosses++;
      }
      cumulativeGrowth += change;
      
      maxGrowth = Math.max(maxGrowth, cumulativeGrowth);
      minGrowth = Math.min(minGrowth, cumulativeGrowth);

      return {
        position: position.position, // Use original position number
        growth: cumulativeGrowth,
        actual: position.actualResult,
        change,
        cumulative: cumulativeGrowth,
        prediction: position.prediction
      };
    });

    const finalGrowth = cumulativeGrowth;
    const totalRange = maxGrowth - minGrowth;
    const maxDrawdown = maxGrowth - minGrowth;

    return {
      growthData: data,
      stats: {
        finalGrowth,
        maxGrowth,
        minGrowth,
        totalRange,
        maxDrawdown,
        totalPositions,
        marketWins,
        marketLosses,
        marketWinRate: totalPositions > 0 ? (marketWins / totalPositions) * 100 : 0
      }
    };
  }, [positionsData]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload as ActualGrowthPoint;
    
    return (
      <div className="bg-background border rounded-lg shadow-lg p-3">
        <p className="font-medium mb-2">Position #{label}</p>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between items-center">
            <span>Market Growth:</span>
            <Badge variant={data.growth >= 0 ? 'default' : 'destructive'}>
              {data.growth > 0 ? '+' : ''}{data.growth}
            </Badge>
          </div>
          <div className="flex justify-between items-center">
            <span>Change:</span>
            <span className={data.change > 0 ? 'text-green-600' : data.change < 0 ? 'text-red-600' : 'text-muted-foreground'}>
              {data.change > 0 ? '+' : ''}{data.change.toFixed(1)}
            </span>
          </div>
          <div className="flex justify-between items-center">
            <span>Market Result:</span>
            <Badge variant={data.actual === 1 ? 'default' : 'destructive'}>
              {data.actual === 1 ? 'WIN' : 'LOSS'}
            </Badge>
          </div>
          {data.prediction && (
            <div className="flex justify-between items-center">
              <span>AI Prediction:</span>
              <Badge variant={data.prediction === 'WIN' ? 'default' : data.prediction === 'LOSS' ? 'destructive' : 'secondary'}>
                {data.prediction}
              </Badge>
            </div>
          )}
        </div>
      </div>
    );
  };

  // Zoom handlers
  const handleMouseDown = useCallback((e: any) => {
    if (e && e.activeLabel) {
      setRefAreaLeft(e.activeLabel);
    }
  }, []);

  const handleMouseMove = useCallback((e: any) => {
    if (refAreaLeft && e && e.activeLabel) {
      setRefAreaRight(e.activeLabel);
    }
  }, [refAreaLeft]);

  const handleMouseUp = useCallback(() => {
    if (refAreaLeft && refAreaRight) {
      const left = Math.min(refAreaLeft, refAreaRight);
      const right = Math.max(refAreaLeft, refAreaRight);
      
      if (Math.abs(right - left) > 1) {
        setZoomDomain({ left, right });
      }
    }
    setRefAreaLeft(null);
    setRefAreaRight(null);
  }, [refAreaLeft, refAreaRight]);

  const handleZoomOut = useCallback(() => {
    setZoomDomain(null);
  }, []);

  const handleReset = useCallback(() => {
    setZoomDomain(null);
    setRefAreaLeft(null);
    setRefAreaRight(null);
  }, []);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Actual Market Growth Chart</CardTitle>
        <CardDescription>
          Market performance based on actual WIN/LOSS results (+{tpRatio} win, -{slRatio} loss)
        </CardDescription>
      </CardHeader>
      <CardContent>
        {/* Statistics Summary */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="p-3 bg-muted/20 rounded-lg">
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-primary" />
              <div>
                <p className="text-sm font-medium">Market Growth</p>
                <p className={`text-xl font-bold ${stats.finalGrowth >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {stats.finalGrowth > 0 ? '+' : ''}{stats.finalGrowth}
                </p>
              </div>
            </div>
          </div>

          <div className="p-3 bg-muted/20 rounded-lg">
            <div className="flex items-center gap-2">
              <TrendingUp className="h-4 w-4 text-green-500" />
              <div>
                <p className="text-sm font-medium">Market Peak</p>
                <p className="text-xl font-bold text-green-600">
                  +{stats.maxGrowth}
                </p>
              </div>
            </div>
          </div>

          <div className="p-3 bg-muted/20 rounded-lg">
            <div className="flex items-center gap-2">
              <TrendingDown className="h-4 w-4 text-red-500" />
              <div>
                <p className="text-sm font-medium">Market Low</p>
                <p className="text-xl font-bold text-red-600">
                  {stats.minGrowth}
                </p>
              </div>
            </div>
          </div>

          <div className="p-3 bg-muted/20 rounded-lg">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Market Win Rate</p>
              <p className="text-xl font-bold text-primary">
                {stats.marketWinRate.toFixed(1)}%
              </p>
              <p className="text-xs text-muted-foreground">
                {stats.marketWins}W / {stats.marketLosses}L
              </p>
            </div>
          </div>
        </div>

        {/* Zoom Controls */}
        <div className="flex gap-2 mb-4">
          <Button
            variant="outline"
            size="sm"
            onClick={handleZoomOut}
            disabled={!zoomDomain}
          >
            <ZoomOut className="h-4 w-4 mr-1" />
            Zoom Out
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleReset}
          >
            <RotateCcw className="h-4 w-4 mr-1" />
            Reset View
          </Button>
          <div className="text-sm text-muted-foreground flex items-center ml-auto">
            {zoomDomain ? 'Zoomed View' : 'Drag to zoom in'}
          </div>
        </div>

        {/* Market Growth Chart */}
        {growthData.length > 0 ? (
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart 
                data={growthData} 
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                onMouseDown={handleMouseDown}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
              >
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis 
                  dataKey="position"
                  type="number"
                  scale="linear"
                  domain={zoomDomain ? [zoomDomain.left, zoomDomain.right] : ['dataMin', 'dataMax']}
                  label={{ value: 'Position Number', position: 'insideBottom', offset: -5 }}
                />
                <YAxis 
                  domain={zoomDomain ? [zoomDomain.bottom, zoomDomain.top] : ['auto', 'auto']}
                  label={{ value: 'Market Growth', angle: -90, position: 'insideLeft' }}
                />
                <Tooltip content={<CustomTooltip />} />
                
                {/* Zero line reference */}
                <ReferenceLine y={0} stroke="hsl(var(--muted-foreground))" strokeDasharray="2 2" />
                
                {/* Profit zone (above 0) */}
                <ReferenceArea 
                  y1={0} 
                  y2={stats.maxGrowth} 
                  fill="hsl(var(--success))" 
                  fillOpacity={0.1} 
                />
                
                {/* Loss zone (below 0) */}
                <ReferenceArea 
                  y1={stats.minGrowth} 
                  y2={0} 
                  fill="hsl(var(--destructive))" 
                  fillOpacity={0.1} 
                />

                {/* Zoom selection area */}
                {refAreaLeft && refAreaRight && (
                  <ReferenceArea
                    x1={refAreaLeft}
                    x2={refAreaRight}
                    strokeOpacity={0.3}
                    fillOpacity={0.3}
                    fill="hsl(var(--primary))"
                  />
                )}
                
                <Line 
                  type="monotone" 
                  dataKey="growth" 
                  stroke="hsl(var(--primary))"
                  strokeWidth={2}
                  dot={false}
                  connectNulls={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="h-80 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <TrendingUp className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>No market data available</p>
              <p className="text-sm">No positions found to calculate market growth</p>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
